import type { ReactNode } from "react";

import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { Crown } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Link, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { useAsyncHref } from "app/components/hooks/useAsyncHref";
import { openHooksModalV5 } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";

import {
    AI_ADS_HOOK_KEY,
    AI_ADS_PLATFORM_META,
    AI_ADS_STATE,
    AI_INSIGHTS_INTERNAL_LINK_TRACKING,
    AI_INSIGHTS_TRANSLATION_KEYS,
    AI_TRAFFIC_HOOK_KEY,
    AI_TRAFFIC_OVERVIEW_STATE,
    ASSISTANT_ICON_META,
    ASSISTANT_ICON_PROPS,
} from "./const";
import { type AiAdsDisplay, normalizeDisplayDomain } from "./getAiInsightsDisplay";

const InsightLink = styled(Link)`
    display: inline;
    color: ${colorsPalettes.blue[500]};
    text-decoration: underline;
    ${setFontVariant("body2")};
`;

const InsightUnlockButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${colorsPalettes.blue[500]};
    text-decoration: underline;
    ${setFontVariant("body2")};
`;

const PlatformLogo = styled.img`
    width: 16px;
    height: 16px;
    display: block;
`;

const trackAiInsightsInternalLinkClick = (eventLabel: string): void => {
    allTrackers.dispatchTrackEvent("internal link", "click", eventLabel);
};

type InsightRowShellProps = {
    label: string;
    dataAutomation: string;
    children: ReactNode;
};

const InsightRowShell = ({ label, dataAutomation, children }: InsightRowShellProps) => (
    <Flex
        flexDirection="column"
        gap="6px"
        data-automation={dataAutomation}
    >
        <Text
            fontVariant="captionTitle"
            color={colorsPalettes.carbon[300]}
        >
            {label}
        </Text>
        <Flex
            alignItems="center"
            gap="6px"
            flexWrap="wrap"
        >
            {children}
        </Flex>
    </Flex>
);

type LockedInsightRowProps = {
    label: string;
    link: string;
    afterLink: string;
    onUnlock: () => void;
    dataAutomation: string;
};

export const LockedInsightRow = ({ label, link, afterLink, onUnlock, dataAutomation }: LockedInsightRowProps) => (
    <InsightRowShell
        label={label}
        dataAutomation={dataAutomation}
    >
        <InsightUnlockButton
            type="button"
            onClick={onUnlock}
            data-automation={`${dataAutomation}-cta`}
        >
            <Crown
                size="xs"
                color={colorsPalettes.indigo[300]}
            />
            {link}
        </InsightUnlockButton>
        <Text
            fontVariant="body2"
            color={colorsPalettes.carbon[500]}
        >
            {afterLink}
        </Text>
    </InsightRowShell>
);

type TrafficInsightRowProps = {
    leadingAssistant: string;
    domain: string;
    country: number | string;
    duration: string;
    webSource: string;
    isWWW: boolean | string;
};

export const TrafficInsightRow = ({
    leadingAssistant,
    domain,
    country,
    duration,
    webSource,
    isWWW,
}: TrafficInsightRowProps) => {
    const { translate } = useTranslate();
    const trafficHref = useAsyncHref(AI_TRAFFIC_OVERVIEW_STATE, {
        key: domain,
        country: String(country),
        duration,
        webSource,
        isWWW,
    });
    const TrafficAssistantIcon = ASSISTANT_ICON_META[leadingAssistant];

    return (
        <InsightRowShell
            label={translate(AI_INSIGHTS_TRANSLATION_KEYS.trafficLabel)}
            dataAutomation="ai-insights-widget-traffic"
        >
            {TrafficAssistantIcon ? <TrafficAssistantIcon {...ASSISTANT_ICON_PROPS} /> : null}
            <Text
                fontVariant="subtitle2"
                color={colorsPalettes.carbon[500]}
            >
                {leadingAssistant === "Others" ? translate(AI_INSIGHTS_TRANSLATION_KEYS.others) : leadingAssistant}
            </Text>
            <Text
                fontVariant="body2"
                color={colorsPalettes.carbon[500]}
            >
                {translate(AI_INSIGHTS_TRANSLATION_KEYS.trafficAfterSource)}
            </Text>
            {trafficHref ? (
                <InsightLink
                    label={translate(AI_INSIGHTS_TRANSLATION_KEYS.trafficLink)}
                    url={trafficHref}
                    tooltip={translate(AI_INSIGHTS_TRANSLATION_KEYS.trafficLinkTooltip)}
                    data-automation="ai-insights-widget-traffic-link"
                    onClick={() => trackAiInsightsInternalLinkClick(AI_INSIGHTS_INTERNAL_LINK_TRACKING.aiSearchTraffic)}
                />
            ) : null}
        </InsightRowShell>
    );
};

type AdsInsightRowProps = {
    adsDisplay: AiAdsDisplay;
    domain: string;
    country: number | string;
    duration: string;
    webSource: string;
    isWWW: boolean | string;
};

export const AdsInsightRow = ({ adsDisplay, domain, country, duration, webSource, isWWW }: AdsInsightRowProps) => {
    const { translate } = useTranslate();
    const adsHref = useAsyncHref(AI_ADS_STATE, {
        key: domain,
        country,
        duration,
        isWWW,
        webSource,
    });
    const adsCountLabel = adsDisplay.isTruncated ? `${adsDisplay.uniqueAdCount}+` : adsDisplay.uniqueAdCount;
    const displayDomain = normalizeDisplayDomain(domain);

    return (
        <InsightRowShell
            label={translate(AI_INSIGHTS_TRANSLATION_KEYS.adsLabel)}
            dataAutomation="ai-insights-widget-ads"
        >
            <Text
                fontVariant="subtitle2"
                color={colorsPalettes.carbon[500]}
                data-automation="ai-insights-widget-ads-domain"
            >
                {displayDomain}
            </Text>
            {adsHref ? (
                <InsightLink
                    label={translate(AI_INSIGHTS_TRANSLATION_KEYS.adsLink, { count: adsCountLabel })}
                    url={adsHref}
                    tooltip={translate(AI_INSIGHTS_TRANSLATION_KEYS.adsLinkTooltip)}
                    data-automation="ai-insights-widget-ads-link"
                    onClick={() => trackAiInsightsInternalLinkClick(AI_INSIGHTS_INTERNAL_LINK_TRACKING.aiAds)}
                />
            ) : (
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon[500]}
                >
                    {translate(AI_INSIGHTS_TRANSLATION_KEYS.adsLink, { count: adsCountLabel })}
                </Text>
            )}
            <Text
                fontVariant="body2"
                color={colorsPalettes.carbon[500]}
            >
                {translate(AI_INSIGHTS_TRANSLATION_KEYS.adsAfterLink)}
            </Text>
            {adsDisplay.platforms.map((platform) => (
                <Tooltip
                    key={platform}
                    content={translate(AI_ADS_PLATFORM_META[platform].nameKey)}
                    data-automation={`ai-insights-widget-ads-platform-${platform}-tooltip`}
                >
                    <PlatformLogo
                        src={AI_ADS_PLATFORM_META[platform].logo}
                        alt={translate(AI_ADS_PLATFORM_META[platform].nameKey)}
                        data-automation={`ai-insights-widget-ads-platform-${platform}`}
                    />
                </Tooltip>
            ))}
        </InsightRowShell>
    );
};

export const TrafficLockedInsightRow = () => {
    const { translate } = useTranslate();

    return (
        <LockedInsightRow
            label={translate(AI_INSIGHTS_TRANSLATION_KEYS.trafficLabel)}
            link={translate(AI_INSIGHTS_TRANSLATION_KEYS.trafficLockedLink)}
            afterLink={translate(AI_INSIGHTS_TRANSLATION_KEYS.trafficLockedAfterLink)}
            onUnlock={() => openHooksModalV5(AI_TRAFFIC_HOOK_KEY)}
            dataAutomation="ai-insights-widget-traffic-locked"
        />
    );
};

export const AdsLockedInsightRow = () => {
    const { translate } = useTranslate();

    return (
        <LockedInsightRow
            label={translate(AI_INSIGHTS_TRANSLATION_KEYS.adsLabel)}
            link={translate(AI_INSIGHTS_TRANSLATION_KEYS.adsLockedLink)}
            afterLink={translate(AI_INSIGHTS_TRANSLATION_KEYS.adsLockedAfterLink)}
            onUnlock={() => openHooksModalV5(AI_ADS_HOOK_KEY)}
            dataAutomation="ai-insights-widget-ads-locked"
        />
    );
};
